import * as React from 'react'

import { cn } from '@/utils'

type TooltipAlign = 'start' | 'center' | 'end'

interface ChartTooltipProps {
  active?: boolean
  position?: { x: number; y: number }
  align?: TooltipAlign
  children?: React.ReactNode
  className?: string
}

const HORIZONTAL_TRANSLATE: Record<TooltipAlign, string> = {
  start: '0',
  center: '-50%',
  end: '-100%',
}

// Keep this much gap between the tooltip and the container edge.
const EDGE_PADDING = 8

export const ChartTooltip = React.memo<ChartTooltipProps>(
  ({ active, position, align = 'center', children, className }) => {
    const ref = React.useRef<HTMLDivElement>(null)
    // Extra horizontal nudge that keeps the tooltip inside its container.
    // Without it, a tooltip anchored to a node at the left or right edge
    // overflows the chart and gets clipped by an ancestor's overflow.
    const [edgeShift, setEdgeShift] = React.useState(0)

    React.useLayoutEffect(() => {
      const tooltip = ref.current
      if (!active || !position || !tooltip) return
      const container = tooltip.offsetParent as HTMLElement | null
      if (!container) return

      // Measure from layout offsets relative to the container. offsetLeft /
      // offsetWidth ignore the CSS transform (the align translate + edgeShift)
      // and are whole pixels, so the natural position is stable no matter what
      // shift is already applied. getBoundingClientRect's subpixel, transformed
      // values drifted between renders and never settled, looping setEdgeShift.
      const width = tooltip.offsetWidth
      const alignOffset =
        align === 'center' ? -width / 2 : align === 'end' ? -width : 0
      const naturalLeft = tooltip.offsetLeft + alignOffset
      const naturalRight = naturalLeft + width

      let shift = 0
      if (naturalLeft < EDGE_PADDING) {
        shift = EDGE_PADDING - naturalLeft
      } else if (naturalRight > container.clientWidth - EDGE_PADDING) {
        shift = container.clientWidth - EDGE_PADDING - naturalRight
      }

      setEdgeShift((previousShift) =>
        Math.round(previousShift) === Math.round(shift) ? previousShift : shift,
      )
    }, [active, position, children, align])

    if (!active || !position) return null

    return (
      <div
        ref={ref}
        className={cn(
          'absolute bg-popover text-popover-foreground text-xs rounded-md border px-2 py-1 pointer-events-none z-50 whitespace-nowrap',
          className,
        )}
        style={{
          left: position.x,
          top: position.y,
          transform: `translate(calc(${HORIZONTAL_TRANSLATE[align]} + ${edgeShift}px), calc(-100% - 10px))`,
        }}
      >
        {children}
      </div>
    )
  },
)
